// src/components/UI.jsx
import React, { useState } from 'react'
import { useApp } from '../context/AppContext'
import { CAT_COLOR, CAT_BG } from '../utils/constants'

/* ── Toast Container ─────────────────────────────────────── */
export function ToastContainer() {
  const { toasts } = useApp()
  return (
    <div className="toast-container">
      {toasts.map(t => (
        <div key={t.id} className={`toast${t.type === 'error' ? ' error' : ''}`}>
          <i className={`ti ti-${t.type === 'error' ? 'alert-triangle' : 'check'}`} aria-hidden="true" />
          {t.msg}
        </div>
      ))}
    </div>
  )
}

/* ── Modal ───────────────────────────────────────────────── */
export function Modal({ open, onClose, title, subtitle, children, maxWidth = 460 }) {
  if (!open) return null
  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth }}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{title}</div>
            {subtitle && <div className="modal-sub">{subtitle}</div>}
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close modal">
            <i className="ti ti-x" aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

/* ── Empty State ─────────────────────────────────────────── */
export function EmptyState({ icon = 'ti-inbox', message, sub, action }) {
  return (
    <div className="empty-state">
      <i className={`ti ${icon}`} aria-hidden="true" />
      <p style={{ fontWeight: 600, marginBottom: 6 }}>{message}</p>
      {sub && <p style={{ color: 'var(--gray-400)', marginBottom: 12 }}>{sub}</p>}
      {action}
    </div>
  )
}

/* ── Category Pill ───────────────────────────────────────── */
export function CatPill({ cat }) {
  return (
    <span
      className="cat-pill"
      style={{ background: CAT_BG[cat] || '#eee', color: CAT_COLOR[cat] || '#555' }}
    >
      {cat}
    </span>
  )
}

/* ── Account Chip ────────────────────────────────────────── */
export function AcctChip({ color, name }) {
  return (
    <span className="acct-chip">
      <span className="dot" style={{ background: color, width: 8, height: 8 }} />
      {name}
    </span>
  )
}

/* ── Type Badge ──────────────────────────────────────────── */
export function TypeBadge({ type }) {
  return (
    <span className={`badge badge-${type}`}>
      {type === 'income' ? 'Income' : type === 'expense' ? 'Expense' : 'Transfer'}
    </span>
  )
}

/* ── Metric Card ─────────────────────────────────────────── */
export function MetricCard({ label, value, color, style }) {
  return (
    <div className="metric-card" style={style}>
      <div className="metric-label">{label}</div>
      <div className="metric-value" style={{ color }}>{value}</div>
    </div>
  )
}

/* ── Metrics Grid ────────────────────────────────────────── */
export function MetricsGrid({ children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 9, marginBottom: 14 }}>
      {children}
    </div>
  )
}

/* ── Section Header ──────────────────────────────────────── */
export function SectionHead({ icon, title, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <div className="card-title" style={{ margin: 0 }}>
        {icon && <i className={`ti ${icon}`} aria-hidden="true" />}
        {title}
      </div>
      {right}
    </div>
  )
}

/* ── Progress Row ────────────────────────────────────────── */
export function ProgressRow({ label, amount, pct, color, prefix = '₹' }) {
  return (
    <div style={{ marginBottom: 11 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
        <CatPill cat={label} />
        <span style={{ color: 'var(--gray-600)', fontSize: 12 }}>
          {pct.toFixed(1)}% — <strong style={{ color }}>{prefix}{amount}</strong>
        </span>
      </div>
      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  )
}

/* ── Confirm Dialog ──────────────────────────────────────── */
export function ConfirmModal({ open, onClose, onConfirm, title = 'Are you sure?', message, confirmLabel = 'Delete', danger = true }) {
  if (!open) return null
  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 360 }}>
        <div className="modal-header">
          <div className="modal-title">{title}</div>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><i className="ti ti-x" aria-hidden="true" /></button>
        </div>
        {message && <p style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 20 }}>{message}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={() => { onConfirm(); onClose() }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ── Search Input ────────────────────────────────────────── */
export function SearchInput({ value, onChange, placeholder = 'Search…' }) {
  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <i className="ti ti-search" aria-hidden="true" style={{ position: 'absolute', left: 9, color: 'var(--gray-400)', fontSize: 14, pointerEvents: 'none' }} />
      <input
        className="input"
        type="search"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ paddingLeft: 30, width: 190, fontSize: 12 }}
      />
    </div>
  )
}

/* ── Filter Pills ────────────────────────────────────────── */
export function FilterPills({ options, labels, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {options.map((opt, i) => (
        <button
          key={opt}
          className={`btn btn-sm${value === opt ? ' btn-primary' : ''}`}
          onClick={() => onChange(opt)}
        >
          {labels ? labels[i] : opt}
        </button>
      ))}
    </div>
  )
}

/* ── Password Input with toggle ──────────────────────────── */
export function PasswordInput({ id, value, onChange, placeholder = 'Password', onKeyDown }) {
  const [show, setShow] = useState(false)
  return (
    <div className="pw-wrap" style={{ position: 'relative' }}>
      <input
        id={id}
        className="input"
        type={show ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        onKeyDown={onKeyDown}
        style={{ paddingRight: 38 }}
        autoComplete="current-password"
      />
      <button className="eye-btn" type="button" onClick={() => setShow(s => !s)} aria-label="Toggle password">
        <i className={`ti ti-${show ? 'eye-off' : 'eye'}`} aria-hidden="true" />
      </button>
    </div>
  )
}
